import React, { PropsWithChildren, useCallback, useEffect, useRef } from 'react';
import { UIStickyContext, UIStickyEvent } from '~/Components/UISticky/UIStickyContext';

const events = ['resize', 'scroll', 'touchstart', 'touchmove', 'touchend', 'pageshow', 'load'] as const;

export function UIStickyContainer({ children, ...props }: PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>) {
  const nodeRef = useRef<HTMLDivElement | null>(null);

  const subscribersRef = useRef<Array<(event: UIStickyEvent) => void>>([]);

  const frameIdRef = useRef<number | null>(null);

  const framePendingRef = useRef(false);

  const subscribe = useCallback((handler: (event: UIStickyEvent) => void) => {
    subscribersRef.current.push(handler);
  }, []);

  const unsubscribe = useCallback((handler: (event: UIStickyEvent) => void) => {
    subscribersRef.current = subscribersRef.current.filter((current) => current !== handler);
  }, []);

  const notifySubscribers = useCallback((evt: Event) => {
    if (framePendingRef.current) {
      return;
    }

    framePendingRef.current = true;

    frameIdRef.current = window.requestAnimationFrame(() => {
      framePendingRef.current = false;

      frameIdRef.current = null;

      const node = nodeRef.current;

      if (!node) {
        return;
      }

      const { top, bottom } = node.getBoundingClientRect();

      const event: UIStickyEvent = {
        distanceFromTop: top,

        distanceFromBottom: bottom,

        eventSource: evt.currentTarget === window ? document.body : node,
      };

      subscribersRef.current.forEach((handler) => handler(event));
    });
  }, []);

  const getParent = useCallback(() => nodeRef.current, []);

  useEffect(() => {
    events.forEach((event) =>
      window.addEventListener(event, notifySubscribers, {
        passive: true,
      }),
    );

    return () => {
      if (frameIdRef.current !== null) {
        window.cancelAnimationFrame(frameIdRef.current);

        frameIdRef.current = null;
      }

      events.forEach((event) => window.removeEventListener(event, notifySubscribers));
    };
  }, [notifySubscribers]);

  return (
    <UIStickyContext.Provider
      value={{
        subscribe,
        unsubscribe,
        getParent,
      }}
    >
      <div
        {...props}
        ref={nodeRef}
        onScroll={notifySubscribers}
        onTouchStart={notifySubscribers}
        onTouchMove={notifySubscribers}
        onTouchEnd={notifySubscribers}
      >
        {children}
      </div>
    </UIStickyContext.Provider>
  );
}
